import React, { useEffect, useState } from 'react';
import {
  AccountInfoType,
  AdvertisingAccountType,
} from '@amzn/aac-chrome-internal-api';
import { AccountDropdownBundleProvider } from 'src/providers/PantherLocalizationProvider';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { AccountInfoState, LoadingStatus } from 'src/commons/types';
import { getChromeData, getNavState } from 'src/commons/apis';
import { isBtswContext, isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { useSelector } from 'react-redux';
import { selectCurrentAccountInfoFromHeader } from 'src/features/header/store/slice';

const fallbackAccountInfo = {
  currentEntityType: '',
  accounts: [],
  currentAccountLink: '',
  currentAccountName: '',
  currentAccountType: AdvertisingAccountType.UNSUPPORTED,
  currentEntityId: '',
  currentMarketplace: '',
  currentMarketplaceId: '',
  accountTypeCounter: 0,
  accountCounter: 0,
  isAdmin: false,
  isManagerAPartner: false,
  hasAccessToDSP: false,
  hasAccessToSA: false,
  loadingStatus: 'success',
} as AccountInfoState;

const AccountDropdownContext =
  React.createContext<AccountInfoState>(fallbackAccountInfo);

export const useAccountDropdownContext = (): AccountInfoState => {
  const context = React.useContext(AccountDropdownContext);
  if (!context) {
    throw new Error(
      'AccountDropdownContext must be used with AccountDropdownProvider',
    );
  }
  return context;
};

export const AccountDropdownProvider: React.FC = ({ children }) => {
  const isInitialAccountInfoLoadedWithBTSW = isBtswContext();

  const [accountInfo, setAccountInfo] = useState<AccountInfoType>(
    {} as AccountInfoType,
  );
  const trimCurrentAccountInfo = useSelector(
    selectCurrentAccountInfoFromHeader,
  ) as AccountInfoType;
  const [detailedLoadingStatus, setDetailedLoadingStatus] =
    useState<LoadingStatus>('loading');

  const accountTypeCounter: number =
    accountInfo && accountInfo.accounts ? accountInfo.accounts.length : 0;
  const accountCounter: number =
    accountInfo && accountInfo.accounts
      ? accountInfo.accounts.reduce(
          (acc, curr) => acc + curr.accounts.length,
          0,
        )
      : 0;

  const setAccountInfoCallback = (newAccountInfo) => {
    // If getNavState call hasn't completed yet - set initialAccountInfo
    if (!accountInfo.accounts) {
      setAccountInfo((accountInfo) => ({
        ...accountInfo,
        ...newAccountInfo,
      }));
    }
    window.AACChrome.InternalAPI.AccountInfoHandler.getInstance().removeCallback(
      setAccountInfoCallback,
    );
    AccountSwitcherPublisher.publishCounterMonitor(
      'accountInfoFailure-getBTSWAccountInfo',
      0,
    );
  };

  // Load InitialAccountInfo from Trim
  const getTrimAccountInfo = () => {
    setAccountInfo((accountInfo) => ({
      ...accountInfo,
      ...trimCurrentAccountInfo,
    }));
  };

  // Load InitialAccountInfo from BTSW
  const getBTSWAccountInfo = () => {
    try {
      // the account info has been set in AccountInfoHandler and can be accessed
      // (i.e. async loading the header will yield this condition)
      const windowAccountInfo =
        window.AACChrome.InternalAPI.AccountInfoHandler.getInstance()
          .accountInfo;
      // accountInfo has not been set by the GlobalNav controller yet and a callback is needed
      if (!windowAccountInfo) {
        window.AACChrome.InternalAPI.AccountInfoHandler.getInstance().registerCallback(
          setAccountInfoCallback,
        );
      } else {
        setAccountInfo((accountInfo) => ({
          ...accountInfo,
          ...windowAccountInfo,
        }));
        AccountSwitcherPublisher.publishCounterMonitor(
          'accountInfoFailure-getBTSWAccountInfo',
          0,
        );
      }
    } catch (e: any) {
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-getBTSWAccountInfo',
        1,
      );
      logger.error('getBTSWAccountInfo failed', e);
    }
  };

  // Ajax load InitialAccountInfo from getChromeData api
  const fetchInitialAccountInto = async () => {
    try {
      const { chromeSharedData } = await getChromeData();
      // If getNavState call hasn't completed yet - set initialAccountInfo
      if (!accountInfo.accounts) {
        setAccountInfo((accountInfo) => ({
          ...accountInfo,
          ...chromeSharedData.currentAccountInfo,
        }));
      }
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchInitialAccountInto',
        0,
      );
    } catch (e: any) {
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchInitialAccountInto',
        1,
      );
      logger.error('fetchInitialAccountInto failed', e);
    }
  };

  // Ajax load detailed AccountInfo from getNavState api
  const fetchDetailedAccountInfo = async () => {
    try {
      const navState = await getNavState();
      setDetailedLoadingStatus('success');
      setAccountInfo((accountInfo) => ({
        ...accountInfo,
        ...navState.accountInfo,
      }));
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        0,
      );
    } catch (e: any) {
      setDetailedLoadingStatus('error');
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        1,
      );
      logger.error('fetchDetailedAccountInfo failed', e);
    }
  };

  useEffect(() => {
    if (isInitialAccountInfoLoadedWithBTSW) {
      getBTSWAccountInfo();
    } else if (isFromTrim() || isFromBtswWc()) {
      getTrimAccountInfo();
    } else {
      fetchInitialAccountInto();
    }
    fetchDetailedAccountInfo();
  }, []);

  // do not render anything until account info is set
  if (Object.keys(accountInfo).length === 0) {
    return null;
  }

  return (
    <AccountDropdownBundleProvider>
      <AccountDropdownContext.Provider
        value={{
          ...accountInfo,
          accountTypeCounter,
          accountCounter,
          loadingStatus: detailedLoadingStatus,
        }}
      >
        {children}
      </AccountDropdownContext.Provider>
    </AccountDropdownBundleProvider>
  );
};
